import { BuildPresentationProps } from '../utils/build-presentation';
import { Slide } from '@features/powerpoint';
import { audienceProfileChartSlide } from '../../visitation/slides/campaign-analytics/audience-profile/audience-profile-chart-slide';
import { campaignAnalyticsTitleSlide } from '../../visitation/slides/campaign-analytics/campaign-analytics-title-slide';
import { CampaignAnalyticsReport } from '@components/Dashboard/CampaignAnalytics/slice/schema';
import { demographicProfileChartSlide } from '../../visitation/slides/campaign-analytics/demographic-profile/demographic-profile-chart-slide';
import { stateAndDmaVisitsTableSlide } from '../../visitation/slides/campaign-analytics/state-and-dma-visits-slide';
import { brandLocationsTableSlide } from '../../visitation/slides/campaign-analytics/brand-locations-slide';
import { proximityToHomeChartSlide } from '../../visitation/slides/campaign-analytics/plain-chart-slides/proximity-to-home-chart-slide';
import { timeToFirstVisitChartSlide } from '../../visitation/slides/campaign-analytics/plain-chart-slides/time-to-first-visit-chart-slide';
import { visitsByTimeOfDayChartSlide } from '../../visitation/slides/campaign-analytics/plain-chart-slides/visits-by-time-of-day-chart-slide';
import { visitsByDayOfWeekChartSlide } from '../../visitation/slides/campaign-analytics/plain-chart-slides/visits-by-day-of-week-chart-slide';
import { footTrafficOverlapChartSlide } from '../../visitation/slides/campaign-analytics/plain-chart-slides/foot-traffic-overlap-chart-slide';

export type CampaignAnalyticsSlideProps = {
  slideMaster: string;
  campaignAnalyticsReport: CampaignAnalyticsReport;
};

const campaignAnalyticsTitleSlideFromProps = (
  props: BuildPresentationProps
): Slide[] =>
  Object.values(props.customFields.includedSlides.campaignAnalytics).some(
    Boolean
  )
    ? [campaignAnalyticsTitleSlide]
    : [];

const audienceProfileChartSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.customFields.includedSlides.campaignAnalytics.audienceProfile &&
    props.campaignAnalyticsReport
    ? audienceProfileChartSlide({
        slideMaster,
        campaignAnalyticsReport: props.campaignAnalyticsReport,
      })
    : [];
};

const demographicProfileChartSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.customFields.includedSlides.campaignAnalytics
    .demographicProfile && props.campaignAnalyticsReport
    ? [
        demographicProfileChartSlide({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];
};

const stateAndDmaVisitsTableSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.customFields.includedSlides.campaignAnalytics
    .stateAndDmaVisits && props.campaignAnalyticsReport
    ? [
        stateAndDmaVisitsTableSlide({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];
};

const brandLocationsTableSlideSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.customFields.includedSlides.campaignAnalytics
    .brandLocationVisits && props.campaignAnalyticsReport
    ? [
        brandLocationsTableSlide({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];
};

const proximityToHomeChartSlideSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.customFields.includedSlides.campaignAnalytics.proximityToHome &&
    props.campaignAnalyticsReport
    ? [
        proximityToHomeChartSlide({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];
};

const timeToFirstVisitChartSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.customFields.includedSlides.campaignAnalytics
    .timeToFirstVisitByDay && props.campaignAnalyticsReport
    ? [
        timeToFirstVisitChartSlide({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];
};

const visitsByTimeOfDayChartSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.customFields.includedSlides.campaignAnalytics
    .visitsByHourOfDay && props.campaignAnalyticsReport
    ? [
        visitsByTimeOfDayChartSlide({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];
};

const visitsByDayOfWeekChartSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.customFields.includedSlides.campaignAnalytics
    .visitsByDayOfWeek && props.campaignAnalyticsReport
    ? [
        visitsByDayOfWeekChartSlide({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];
};

const footTrafficOverlapChartSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.customFields.includedSlides.campaignAnalytics
    .footTrafficOverlap && props.campaignAnalyticsReport
    ? [
        footTrafficOverlapChartSlide({
          slideMaster,
          campaignAnalyticsReport: props.campaignAnalyticsReport,
        }),
      ]
    : [];
};

export const campaignAnalyticsSlides = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  const slides = props.campaignAnalyticsReport
    ? [
        campaignAnalyticsTitleSlideFromProps(props),
        audienceProfileChartSlideFromProps(props, slideMaster),
        demographicProfileChartSlideFromProps(props, slideMaster),
        stateAndDmaVisitsTableSlideFromProps(props, slideMaster),
        brandLocationsTableSlideSlideFromProps(props, slideMaster),
        proximityToHomeChartSlideSlideFromProps(props, slideMaster),
        timeToFirstVisitChartSlideFromProps(props, slideMaster),
        visitsByTimeOfDayChartSlideFromProps(props, slideMaster),
        visitsByDayOfWeekChartSlideFromProps(props, slideMaster),
        footTrafficOverlapChartSlideFromProps(props, slideMaster),
      ].flat()
    : [];

  return slides;
};
